import { FCElement } from '@fc/component';
import { resetShimmer } from '@fc/utilities';
import {
  getCartLineQuantity,
  patchCartSectionMarkup,
  getQuantityAdjustedMessage,
  resolveCartSectionsUrl,
  submitCartSectionsMutation,
} from '@fc/cart';
import { buildSectionSelector, sectionManager } from '@fc/sections';
import {
  FCEvents,
  CartRefreshEvent,
  CartSectionsRenderedEvent,
  DiscountChangeEvent,
  isCartAddSuccessEvent,
} from '@fc/events';
import { cartPerformance } from '@fc/metrics';
import { isAbortError, LatestRequestController } from '@fc/section-response';

const CART_DISABLED_CLASS = 'cart-items-disabled';
const REMOVING_CLASS = 'removing';
const ROW_PENDING_CLASS = 'cart-items__table-row--pending';
const ROW_PENDING_QUANTITY_CLASS = 'cart-items__table-row--quantity-pending';
const ROW_PENDING_REMOVE_CLASS = 'cart-items__table-row--remove-pending';

class CartItemsComponent extends FCElement {
  #qtyHandler = (evt) => this.#handleQuantityChange(evt);
  #updateRequest = new LatestRequestController();
  #isBusy = false;
  #disabledControlStates = new WeakMap();
  #disabledHostStates = new WeakMap();
  #activeMutation = null;

  connectedCallback() {
    super.connectedCallback();
    this.#bindEventListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#updateRequest.abort();
    this.#unbindEventListeners();
  }

  get sectionId() {
    const id = this.dataset.sectionId;
    if (!id) throw new Error('Section id missing');
    return id;
  }

  handleDiscountUpdate = (event) => this.#handleCartSync(event);

  onLineItemRemove(line, event) {
    event?.preventDefault();
    if (this.#isBusy) return;
    this.#removeLineItems(line, null, this.#captureFocusSnapshot({ line, controlType: 'remove' }));
  }

  #bindEventListeners() {
    document.addEventListener(FCEvents.cartRefresh, this.#handleCartSync);
    document.addEventListener(FCEvents.discountChange, this.handleDiscountUpdate);
    document.addEventListener(FCEvents.qtyChange, this.#qtyHandler);
  }

  #unbindEventListeners() {
    document.removeEventListener(FCEvents.cartRefresh, this.#handleCartSync);
    document.removeEventListener(FCEvents.discountChange, this.handleDiscountUpdate);
    document.removeEventListener(FCEvents.qtyChange, this.#qtyHandler);
  }

  #handleQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    if (this.#isBusy) {
      event.preventDefault?.();
      return;
    }

    const detail = event?.detail;
    const line = Number(detail?.cartLine);
    const quantity = Number(detail?.quantity);
    const key = typeof detail?.cartKey === 'string' && detail.cartKey ? detail.cartKey : null;

    if (!Number.isInteger(line) || line < 1) return;
    if (!Number.isFinite(quantity) || quantity < 0) return;

    const focusSnapshot = this.#captureFocusSnapshot({ line, key });

    if (quantity === 0) {
      this.#clearLineMessage(line);
      this.#removeLineItems(line, key, focusSnapshot);
      return;
    }

    this.#clearLineMessage(line);
    this.#submitQuantityChange({
      line,
      key,
      quantity,
      action: 'change',
      focusSnapshot,
      mutationState: this.#captureMutationState({ line, key, quantity, action: 'change' }),
    });
  }

  #shimmerLineTotal(line) {
    const row = this.#getLineRow(line);
    row?.querySelector('shimmer-text')?.shimmer();
  }

  #shimmerCartTotals() {
    for (const total of this.#cartTotalElements()) {
      if (typeof total?.shimmer === 'function') total.shimmer();
    }
  }

  #cartTotalElements() {
    const totals = new Set();
    const refTotal = this.refs.cartTotal;

    if (Array.isArray(refTotal)) {
      refTotal.forEach((total) => totals.add(total));
    } else if (refTotal) {
      totals.add(refTotal);
    }

    for (const total of this.querySelectorAll('[data-ref="cartTotal"], [data-cart-subtotal]')) {
      totals.add(total);
    }

    return [...totals].filter((total) => total instanceof HTMLElement);
  }

  #removeLineItems(line, key = null, focusSnapshot = null) {
    if (this.#isBusy) return;
    const lineKey = key || this.#getLineRow(line)?.dataset.key || null;
    const mutationState = this.#captureMutationState({
      line,
      key: lineKey,
      quantity: 0,
      action: 'clear',
    });

    this.#submitQuantityChange({
      line,
      key: lineKey,
      quantity: 0,
      action: 'clear',
      focusSnapshot: focusSnapshot ?? this.#captureFocusSnapshot({ line, key: lineKey, controlType: 'remove' }),
      mutationState,
    });
  }

  #collectRowsForRemoval(line) {
    const target = this.#getLineRow(line);
    if (!target) return [];

    return [target, ...this.#cartRows().filter((row) => row.dataset.parentKey === target.dataset.key)];
  }

  #collapseRowWhilePending(row) {
    row.style.setProperty('--row-height', `${row.clientHeight}px`);
    row.classList.add(REMOVING_CLASS);
  }

  #getLineRow(line) {
    return this.#cartRows()[line - 1];
  }

  #cartRows() {
    return Array.isArray(this.refs.cartItemRows) ? this.refs.cartItemRows : [];
  }

  async #submitQuantityChange({ line, key = null, quantity, action, focusSnapshot = null, mutationState = null }) {
    const marker = cartPerformance.createStartingMarker(`${action}:user-action`);
    const request = this.#updateRequest.start();
    const restoreSnapshot = focusSnapshot ?? this.#captureFocusSnapshot({ line, key });
    const activeMutation = mutationState ?? this.#captureMutationState({ line, key, quantity, action });
    this.#setBusy(true);
    this.#beginPendingMutation(activeMutation);

    try {
      const result = await this.#requestCartUpdate({ line, key, quantity, action, signal: request.signal });
      if (!request.isCurrent()) return;

      resetShimmer(this);

      if (result.payload?.errors) {
        const message = this.#normalizeLineMessage(result.payload.errors);
        if (action === 'clear') {
          await this.#refreshCartAfterMutationError({ line, message, request, mutationState: activeMutation });
        } else {
          this.#renderLineError(line, message, activeMutation);
        }
        return;
      }

      const actualQuantity = getCartLineQuantity(result.payload, line);
      const adjustmentMessage =
        action === 'change' &&
        actualQuantity !== null &&
        actualQuantity !== quantity
          ? getQuantityAdjustedMessage(actualQuantity)
          : '';

      this.#emitRefreshEvent(result);
      await this.#renderCurrentSection(result.sectionMarkup, action);
      this.#clearMutationControlSnapshots();

      if (adjustmentMessage) {
        this.#queueLineMessage(line, adjustmentMessage);
      }
    } catch (error) {
      if (!isAbortError(error)) {
        const message = this.#mutationErrorMessage(error);
        if (message) {
          await this.#refreshCartAfterMutationError({ line, message, request, mutationState: activeMutation });
        } else {
          console.error(error); // eslint-disable-line no-console
        }
      }
    } finally {
      if (request.isCurrent()) {
        this.#clearPendingMutation();
        this.#setBusy(false);
        this.#restoreFocusAfterRender(restoreSnapshot, action);
      }
      request.finish();
      cartPerformance.measureFromMarker(marker);
    }
  }

  async #requestCartUpdate({ line, key = null, quantity, action, signal }) {
    const payload = key ? { id: key, quantity } : { line, quantity };

    return submitCartSectionsMutation(
      FitCheck.routes.cart_change_url,
      payload,
      {
        root: document,
        sectionId: this.sectionId,
        sectionIds: this.#collectSectionIds(),
        sectionsUrl: resolveCartSectionsUrl(),
        performanceAction: action,
        signal,
      },
    );
  }

  #captureMutationState({ line, key = null, quantity, action }) {
    const row = this.#getLineRow(line);
    const selector = this.refs.quantitySelectors?.[line - 1];
    const input = selector?.querySelector('input');
    const previousQuantity = this.#previousQuantityForInput(input);
    const rows = action === 'clear' ? this.#collectRowsForRemoval(line) : row instanceof HTMLElement ? [row] : [];

    return {
      action,
      key: key || row?.dataset?.key || null,
      line,
      previousQuantity,
      quantity,
      rows,
    };
  }

  #previousQuantityForInput(input) {
    if (!(input instanceof HTMLInputElement)) return null;

    const cartQuantity = Number.parseInt(String(input.dataset.cartQuantity ?? ''), 10);
    if (Number.isFinite(cartQuantity) && cartQuantity >= 0) return cartQuantity;

    const defaultQuantity = Number.parseInt(String(input.defaultValue ?? ''), 10);
    if (Number.isFinite(defaultQuantity) && defaultQuantity >= 0) return defaultQuantity;

    const currentQuantity = Number.parseInt(String(input.value ?? ''), 10);
    return Number.isFinite(currentQuantity) && currentQuantity >= 0 ? currentQuantity : null;
  }

  #beginPendingMutation(mutationState) {
    if (!mutationState) return;

    this.#activeMutation = mutationState;
    this.#shimmerCartTotals();

    if (mutationState.action === 'change') {
      this.#applyPendingQuantityState(mutationState);
      return;
    }

    if (mutationState.action === 'clear') {
      this.#applyPendingRemoveState(mutationState);
    }
  }

  #applyPendingQuantityState(mutationState) {
    const row = this.#getLineRow(mutationState.line);
    const selector = this.refs.quantitySelectors?.[mutationState.line - 1];
    const input = selector?.querySelector('input');

    if (input instanceof HTMLInputElement) {
      input.value = String(mutationState.quantity);
      if (mutationState.previousQuantity !== null) {
        input.defaultValue = String(mutationState.previousQuantity);
      }
    }

    if (selector instanceof HTMLElement) {
      selector.dataset.quantityState = 'busy';
    }

    if (row instanceof HTMLElement) {
      this.#markRowPending(row, 'quantity');
      this.#shimmerLineTotal(mutationState.line);
    }
  }

  #applyPendingRemoveState(mutationState) {
    for (const row of mutationState.rows) {
      if (!(row instanceof HTMLElement)) continue;
      this.#markRowPending(row, 'remove');
      this.#collapseRowWhilePending(row);
    }
  }

  #markRowPending(row, type) {
    row.dataset.cartRowPending = type;
    row.setAttribute('aria-busy', 'true');
    row.classList.add(ROW_PENDING_CLASS);
    row.classList.toggle(ROW_PENDING_QUANTITY_CLASS, type === 'quantity');
    row.classList.toggle(ROW_PENDING_REMOVE_CLASS, type === 'remove');
  }

  #clearPendingMutation() {
    const pendingRows = new Set([
      ...(this.#activeMutation?.rows || []),
      ...this.querySelectorAll(`.${ROW_PENDING_CLASS}`),
    ]);

    for (const row of pendingRows) {
      if (!(row instanceof HTMLElement)) continue;
      delete row.dataset.cartRowPending;
      row.removeAttribute('aria-busy');
      row.classList.remove(ROW_PENDING_CLASS, ROW_PENDING_QUANTITY_CLASS, ROW_PENDING_REMOVE_CLASS, REMOVING_CLASS);
      row.style.removeProperty('--row-height');
    }

    this.#activeMutation = null;
  }

  #rollbackQuantityInput(line, mutationState = this.#activeMutation) {
    if (!mutationState || mutationState.action !== 'change' || mutationState.line !== line) return;

    const selector = this.refs.quantitySelectors?.[line - 1];
    const input = selector?.querySelector('input');
    if (!(input instanceof HTMLInputElement)) return;
    if (mutationState.previousQuantity === null) return;

    const previousQuantity = String(mutationState.previousQuantity);
    input.value = previousQuantity;
    input.defaultValue = previousQuantity;
    input.setAttribute('value', previousQuantity);
  }

  #collectSectionIds() {
    return [this.sectionId];
  }

  async #refreshCartAfterMutationError({ line, message, request, mutationState = null }) {
    try {
      const sectionHtml = await sectionManager.getSectionHTML(this.sectionId, {
        cache: false,
        signal: request.signal,
      });
      if (!request.isCurrent()) return;

      resetShimmer(this);
      await this.#renderCurrentSection(sectionHtml);
      this.#clearMutationControlSnapshots();
      this.#queueLineMessage(line, message);
    } catch (refreshError) {
      if (!isAbortError(refreshError)) {
        this.#renderLineError(line, message, mutationState);
      }
    }
  }

  #emitRefreshEvent(result) {
    this.dispatchEvent(
      new CartRefreshEvent(result.payload ?? {}, this.sectionId, {
        itemCount: result.itemCount ?? 0,
        source: 'cart-items',
        sections: result.sections,
      }),
    );
  }

  async #renderCurrentSection(sectionHtml, action = null) {
    const marker = action ? cartPerformance.createStartingMarker(`${action}:section-patch`) : null;

    try {
      if (sectionHtml) {
        patchCartSectionMarkup(this.sectionId, sectionHtml);
        await this.#waitForSectionMorphSync();
        return;
      }

      if (this.#hasMountedSectionRoot()) {
        await sectionManager.renderSection(this.sectionId, { cache: false });
      } else {
        const nextSectionHtml = await sectionManager.getSectionHTML(this.sectionId, { cache: false });
        patchCartSectionMarkup(this.sectionId, nextSectionHtml);
      }
      await this.#waitForSectionMorphSync();
    } finally {
      cartPerformance.measureFromMarker(marker);
    }
  }

  #hasMountedSectionRoot() {
    try {
      return document.getElementById(buildSectionSelector(this.sectionId)) instanceof HTMLElement;
    } catch {
      return false;
    }
  }

  async #waitForSectionMorphSync() {
    await Promise.resolve();
  }

  #renderLineError(line, message, mutationState = null) {
    const normalizedMessage = this.#normalizeLineMessage(message);
    this.#rollbackQuantityInput(line, mutationState);
    const selector = this.refs.quantitySelectors?.[line - 1];
    const input = selector?.querySelector('input');
    if (input) {
      input.value = input.defaultValue;
    }

    if (selector instanceof HTMLElement) {
      const quantityState = input?.disabled ? 'disabled' : 'adjusted';
      selector.dataset.quantityState = quantityState;

      const pendingHostState = this.#disabledHostStates.get(selector);
      if (pendingHostState) {
        pendingHostState.quantityState = quantityState;
      }
    }

    this.#showLineMessage(line, normalizedMessage);
  }

  #mutationErrorMessage(error) {
    return this.#normalizeLineMessage(error?.payload?.description || error?.payload?.message || error?.message);
  }

  #normalizeLineMessage(message) {
    if (typeof message === 'string') return message.trim();
    if (Array.isArray(message)) return message.map((entry) => this.#normalizeLineMessage(entry)).filter(Boolean).join(' ');
    if (message && typeof message === 'object') {
      return Object.values(message)
        .map((entry) => this.#normalizeLineMessage(entry))
        .filter(Boolean)
        .join(' ');
    }

    return '';
  }

  #handleCartSync = async (event) => {
    const focusSnapshot = this.#captureFocusSnapshot();
    const action = isCartAddSuccessEvent(event) ? 'add' : null;

    if (event instanceof DiscountChangeEvent) {
      await this.#renderCurrentSection(this.#sectionMarkupFromEvent(event));
      this.#restoreFocusAfterRender(focusSnapshot);
      this.#emitSectionsRendered(event);
      return;
    }

    if (event.target === this) return;

    await this.#renderCurrentSection(event.detail?.data?.sections?.[this.sectionId], action);
    this.#restoreFocusAfterRender(focusSnapshot, action);
    this.#emitSectionsRendered(event);
    this.#measureCartAddUserAction(event);
  };

  #captureFocusSnapshot(options = {}) {
    const activeElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const row = activeElement?.closest?.('.cart-items__table-row');
    const line = this.#normalizeLineNumber(options.line) ?? this.#lineNumberFromElement(activeElement, row);
    const key =
      (typeof options.key === 'string' && options.key ? options.key : null) ||
      activeElement?.closest?.('[data-cart-key]')?.dataset?.cartKey ||
      row?.dataset?.key ||
      null;
    const dialog = this.closest('dialog');

    return {
      activeElement,
      controlType: options.controlType || this.#controlTypeFromElement(activeElement),
      context: dialog instanceof HTMLDialogElement ? 'drawer' : 'page',
      key,
      line,
      sectionId: this.sectionId,
      wasDrawerOpen: dialog instanceof HTMLDialogElement && dialog.open,
      wasInCart: activeElement instanceof HTMLElement && this.contains(activeElement),
    };
  }

  #normalizeLineNumber(line) {
    const value = Number(line);
    return Number.isInteger(value) && value > 0 ? value : null;
  }

  #lineNumberFromElement(element, row) {
    const cartLine = this.#normalizeLineNumber(element?.closest?.('[data-cart-line]')?.dataset?.cartLine);
    if (cartLine) return cartLine;

    if (row instanceof HTMLElement) {
      const index = this.#cartRows().indexOf(row);
      if (index >= 0) return index + 1;
    }

    return null;
  }

  #controlTypeFromElement(element) {
    if (!(element instanceof HTMLElement)) return 'container';
    if (element.matches('[data-ref="noteInput"], .cart-note__instructions')) return 'note';
    if (element.matches('.cart-discount__input')) return 'discount-input';
    if (element.closest('.cart-discount__button')) return 'discount-submit';
    if (element.closest('.cart-discount__pill-remove')) return 'discount-remove';
    if (element.matches('input[data-cart-line]')) return 'quantity-input';

    const button = element.closest('button');
    if (button?.matches('.cart-items__remove')) return 'remove';
    if (button?.matches('.quantity-plus, [name="plus"]')) return 'quantity-plus';
    if (button?.matches('.quantity-minus, [name="minus"]')) return 'quantity-minus';
    if (button?.matches('.cart-discount__button')) return 'discount-submit';
    if (button?.matches('.cart-discount__pill-remove')) return 'discount-remove';

    return 'container';
  }

  #restoreFocusAfterRender(snapshot, action = null) {
    cartPerformance.measure(action ? `${action}:focus-restore` : null, () => {
      if (!snapshot?.wasInCart) return;
      if (this.#hasMeaningfulConnectedFocus()) return;

      const root = this.#renderedCartComponent(snapshot);
      const target = this.#resolveFocusTarget(root, snapshot);
      this.#focusCartTarget(target);
    });
  }

  #hasMeaningfulConnectedFocus() {
    const activeElement = document.activeElement;
    return (
      activeElement instanceof HTMLElement &&
      activeElement !== document.body &&
      activeElement !== document.documentElement &&
      activeElement.isConnected
    );
  }

  #renderedCartComponent(snapshot) {
    const sectionId = snapshot?.sectionId || this.sectionId;
    let sectionRoot = null;

    try {
      sectionRoot = document.getElementById(buildSectionSelector(sectionId));
    } catch {
      sectionRoot = null;
    }

    const selector = `cart-items[data-section-id="${this.#cssEscape(sectionId)}"]`;
    const rendered = sectionRoot?.querySelector(selector);
    if (rendered instanceof HTMLElement) return rendered;
    return this.isConnected ? this : null;
  }

  #resolveFocusTarget(root, snapshot) {
    if (!(root instanceof HTMLElement)) return null;

    const lineTarget = this.#resolveLineControl(root, snapshot);
    if (lineTarget) return lineTarget;

    const matchingControl = this.#resolveNonLineControl(root, snapshot.controlType);
    if (matchingControl) return matchingControl;

    const emptyTarget = this.#resolveEmptyCartTarget(root);
    if (emptyTarget) return emptyTarget;

    const heading = this.#resolveCartHeading(root, snapshot);
    if (heading) return heading;

    const closeButton = root.closest('cart-drawer')?.querySelector('[data-ref="closeButton"], .cart-drawer__close-button');
    if (closeButton instanceof HTMLElement) return closeButton;

    return root;
  }

  #resolveLineControl(root, snapshot) {
    if (!snapshot?.line && !snapshot?.key) return null;
    if (!['quantity-input', 'quantity-plus', 'quantity-minus', 'remove'].includes(snapshot.controlType)) return null;

    for (const row of this.#lineFocusRows(root, snapshot)) {
      const control = this.#controlInRow(row, snapshot.controlType);
      if (control) return control;
    }

    return null;
  }

  #lineFocusRows(root, snapshot) {
    const rows = [...root.querySelectorAll('.cart-items__table-row')].filter((row) => row instanceof HTMLElement);
    const sameRow = snapshot.key ? rows.find((row) => row.dataset.key === snapshot.key) : null;
    const sameIndex = sameRow ? rows.indexOf(sameRow) : -1;
    const candidates = [];

    if (sameRow) {
      candidates.push(sameRow);
    } else if (snapshot.line) {
      candidates.push(rows[snapshot.line - 1]);
    }

    if (snapshot.line || sameIndex >= 0) {
      candidates.push(rows[sameIndex >= 0 ? sameIndex + 1 : snapshot.line - 1]);
      candidates.push(rows[sameIndex >= 0 ? sameIndex - 1 : snapshot.line - 2]);
    }

    return [...new Set(candidates)].filter((row) => row instanceof HTMLElement);
  }

  #controlInRow(row, controlType) {
    const selectors = {
      'quantity-input': 'qty-selector input:not([disabled])',
      'quantity-plus': 'button.quantity-plus:not([disabled]), button[name="plus"]:not([disabled])',
      'quantity-minus': 'button.quantity-minus:not([disabled]), button[name="minus"]:not([disabled])',
      remove: '.cart-items__remove:not([hidden]):not([disabled])',
    };

    return this.#firstUsable(row, selectors[controlType]);
  }

  #resolveNonLineControl(root, controlType) {
    const selectors = {
      note: '[data-ref="noteInput"]:not([disabled]), .cart-note__instructions:not([disabled])',
      'discount-input': '.cart-discount__input:not([disabled])',
      'discount-submit': '.cart-discount__button:not([disabled])',
      'discount-remove': '.cart-discount__pill-remove:not([disabled])',
    };

    return this.#firstUsable(root, selectors[controlType]);
  }

  #resolveEmptyCartTarget(root) {
    return (
      this.#firstUsable(root, '.cart-items__empty-button, .cart-section__empty-state a[href]') ||
      this.#firstUsable(root, '.cart-section__empty-state, .cart-drawer__content, .cart-items__wrapper')
    );
  }

  #resolveCartHeading(root, snapshot) {
    if (snapshot.context === 'drawer' || snapshot.wasDrawerOpen) {
      const heading = root.closest('cart-drawer')?.querySelector('.cart-drawer__heading');
      if (heading instanceof HTMLElement) return heading;
    }

    return this.#firstUsable(root, '.cart-title, .cart-section__title h1, .cart-section__title');
  }

  #firstUsable(root, selector) {
    if (!(root instanceof Element) || !selector) return null;
    return [...root.querySelectorAll(selector)].find((element) => this.#canReceiveFocus(element)) ?? null;
  }

  #canReceiveFocus(element) {
    if (!(element instanceof HTMLElement)) return false;
    if (element.hidden || element.closest('[hidden], [inert]')) return false;
    if (element.getAttribute('aria-disabled') === 'true') return false;
    if ('disabled' in element && element.disabled) return false;
    return true;
  }

  #focusCartTarget(target) {
    if (!(target instanceof HTMLElement)) return;

    const hadTabIndex = target.hasAttribute('tabindex');
    if (!this.#isNaturallyFocusable(target) && !hadTabIndex) {
      target.setAttribute('tabindex', '-1');
      target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
    }

    try {
      target.focus({ preventScroll: true });
    } catch {
      target.focus();
    }
  }

  #isNaturallyFocusable(element) {
    return element.matches('a[href], button, input, select, textarea, summary, [tabindex]');
  }

  #cssEscape(value) {
    if (window.CSS?.escape) return window.CSS.escape(String(value));
    return String(value).replace(/["\\]/g, '\\$&');
  }

  #setBusy(isBusy) {
    this.#isBusy = Boolean(isBusy);
    this.classList.toggle(CART_DISABLED_CLASS, this.#isBusy);
    if (this.#isBusy) {
      this.setAttribute('aria-busy', 'true');
    } else {
      this.removeAttribute('aria-busy');
    }
    this.#syncMutationControls(this.#isBusy);
  }

  #syncMutationControls(isBusy) {
    const controls = this.#mutationControls();
    const hosts = this.#quantitySelectorHosts();

    if (isBusy) {
      for (const host of hosts) {
        if (!this.#disabledHostStates.has(host)) {
          this.#disabledHostStates.set(host, {
            ariaDisabled: host.getAttribute('aria-disabled'),
            quantityState: host.dataset.quantityState || '',
          });
        }

        host.setAttribute('aria-disabled', 'true');
        host.dataset.quantityState = 'busy';
      }

      for (const control of controls) {
        if (!this.#disabledControlStates.has(control)) {
          this.#disabledControlStates.set(control, {
            disabled: control.disabled,
            ariaDisabled: control.getAttribute('aria-disabled'),
          });
        }

        control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
      }

      return;
    }

    for (const host of hosts) {
      const previous = this.#disabledHostStates.get(host);
      if (!previous) continue;

      if (previous.ariaDisabled === null) {
        host.removeAttribute('aria-disabled');
      } else {
        host.setAttribute('aria-disabled', previous.ariaDisabled);
      }

      if (previous.quantityState) {
        host.dataset.quantityState = previous.quantityState;
      } else {
        delete host.dataset.quantityState;
      }

      this.#disabledHostStates.delete(host);
    }

    for (const control of controls) {
      const previous = this.#disabledControlStates.get(control);
      if (!previous) continue;

      control.disabled = previous.disabled;
      if (previous.ariaDisabled === null) {
        control.removeAttribute('aria-disabled');
      } else {
        control.setAttribute('aria-disabled', previous.ariaDisabled);
      }

      this.#disabledControlStates.delete(control);
    }
  }

  #mutationControls() {
    return [...this.querySelectorAll('.cart-items__quantity button, .cart-items__quantity input, .cart-items__remove')].filter(
      (element) => element instanceof HTMLButtonElement || element instanceof HTMLInputElement,
    );
  }

  #quantitySelectorHosts() {
    return [...this.querySelectorAll('qty-selector')].filter((element) => element instanceof HTMLElement);
  }

  #clearMutationControlSnapshots() {
    this.#disabledControlStates = new WeakMap();
    this.#disabledHostStates = new WeakMap();
  }

  #emitSectionsRendered(event) {
    document.dispatchEvent(
      new CartSectionsRenderedEvent(event.detail?.resource ?? null, event.detail?.sourceId ?? null, event.detail?.data ?? {}, {
        sectionId: this.sectionId,
      }),
    );
  }

  #sectionMarkupFromEvent(event) {
    const sectionId = this.sectionId;
    const dataSections = event.detail?.data?.sections;
    if (dataSections && typeof dataSections[sectionId] === 'string') return dataSections[sectionId];

    const resourceSections = event.detail?.resource?.sections;
    return typeof resourceSections?.[sectionId] === 'string' ? resourceSections[sectionId] : null;
  }

  #measureCartAddUserAction(event) {
    if (!isCartAddSuccessEvent(event)) return;
    const marker = cartPerformance.getUserActionMarker(event.detail?.data);
    const afterEventDispatch = globalThis.queueMicrotask || ((callback) => Promise.resolve().then(callback));
    afterEventDispatch(() => cartPerformance.measureFromMarker(marker));
  }

  #queueLineMessage(line, message) {
    requestAnimationFrame(() => {
      this.#showLineMessage(line, message);
    });
  }

  #showLineMessage(line, message) {
    const selector = this.refs.quantitySelectors?.[line - 1];
    const input = selector?.querySelector('input');
    const errorElement = this.refs[`cartItemError-${line}`];
    const containerElement = this.refs[`cartItemErrorContainer-${line}`];
    if (!(errorElement instanceof HTMLElement) || !(containerElement instanceof HTMLElement)) return;

    if (selector instanceof HTMLElement) {
      selector.dataset.quantityState = input?.disabled ? 'disabled' : 'adjusted';
    }

    if (input instanceof HTMLInputElement) {
      input.defaultValue = input.value;
      input.setAttribute('aria-invalid', 'true');
    }

    errorElement.textContent = message;
    containerElement.classList.remove('hidden');
  }

  #clearLineMessage(line) {
    const selector = this.refs.quantitySelectors?.[line - 1];
    const input = selector?.querySelector('input');
    const errorElement = this.refs[`cartItemError-${line}`];
    const containerElement = this.refs[`cartItemErrorContainer-${line}`];

    if (selector instanceof HTMLElement) {
      selector.dataset.quantityState = input?.disabled ? 'disabled' : 'ready';
    }

    if (errorElement instanceof HTMLElement) {
      errorElement.textContent = '';
    }

    if (containerElement instanceof HTMLElement) {
      containerElement.classList.add('hidden');
    }

    if (input instanceof HTMLInputElement) {
      input.removeAttribute('aria-invalid');
    }
  }
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItemsComponent);
}
